/**
 * BookingFormPage - Create manual booking on behalf of studio
 */

import { useState, useEffect, useMemo } from 'react';
import { parseLocalDate } from '../../../utils/date';
import { useNavigate, Link } from 'react-router-dom';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import {
  createManualBooking,
  getStudios,
  getAllTourStops,
  getShow,
  formatCurrency,
  type Studio,
  type TourStop,
  type ClassOption,
} from '../../../api/admin';
import { getEventPricing, getPublicSystemSettings, getEventAvailability, type PublicSystemSettings, type TimeSlot } from '../../../api/events';
import {
  formatClassLevel,
  CLASS_LEVEL_PICKER_OPTIONS,
  type SelectableClassLevel,
} from '../../../utils/classLevel';
import {
  isMatineeThresholdTriggered,
  formatTimeDisplay,
  resolveSessionTimes,
  deriveBookingMode,
  resolveBookingClassTimes,
  type ResolvedSessionTimes,
} from '@sda/shared';

type SessionMode = 'session_1' | 'session_2' | 'both';
type SimultaneousTrigger = 'matinee_threshold' | 'manual_checkbox' | 'distance_threshold' | null;

/**
 * Availability time_slot keys used for capacity-warning lookups only.
 * These match the literal string keys returned by the availability API for
 * Sat/Sun slots. Do NOT use for display — use radioLabels / resolveBookingClassTimes.
 */
const SESSION_SLOT_KEY: Record<'session_1' | 'session_2', string> = {
  session_1: '9:00 AM',
  session_2: '11:00 AM',
};

/**
 * Haversine distance in miles between two lat/lng points. Used to pre-rank
 * tour stops in the typeahead by distance from the selected studio — the
 * authoritative travel-surcharge distance still comes from /events/:id/pricing
 * (Google Maps driving distance), this is just a fast client-side estimate.
 */
function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 3959;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Returns the later of two date-only ISO strings (YYYY-MM-DD).
 * Used for the Class Date min= attribute: we want max(stop.start_date, today)
 * so in-progress stops don't allow past dates. Falls back to today when
 * start_date is undefined (stop not yet selected).
 */
export function maxIsoDate(a: string | undefined, b: string): string {
  if (!a) return b;
  return a > b ? a : b;
}

export interface CapacityWarningParams {
  selectedDate: string;
  isSelectedDateMonday: boolean;
  sessionMode: SessionMode;
  /** Entered student count (already parsed to a number; 0 when blank). */
  studentCount: number;
  /**
   * Real per-class student capacity from the selected class option
   * (DB max_students, exposed to the frontend as max_participants).
   * Null/undefined when unknown → the student check is skipped.
   */
  maxStudents: number | null | undefined;
  selectedMondaySlotKey: string;
  selectedMondaySlotObj: TimeSlot | undefined;
  availabilitySlots: TimeSlot[];
}

/**
 * Derive informational capacity warnings for the picked date/session.
 *
 * Non-blocking by design: admin manual bookings bypass capacity. The notice
 * distinguishes two INDEPENDENT concerns that must not be conflated:
 *   - Faculty / session-slot availability (is_closed, sold_out). These come from
 *     the availability API's `remaining`/`sold_out`, which count how many more
 *     CLASSES fit (faculty capacity) — NOT student seats.
 *   - Per-class student seats — the class option's max_students (surfaced as
 *     max_participants). This is the real "up to 40 students per class" limit.
 *
 * SDA-227: the old logic compared the entered student count against `remaining`
 * (class slots), a category error that flagged e.g. "15 students / 3 spots →
 * overbooking by 12". The student check is now driven by maxStudents instead.
 */
export function computeCapacityWarnings(params: CapacityWarningParams): string[] {
  const {
    selectedDate,
    isSelectedDateMonday,
    sessionMode,
    studentCount,
    maxStudents,
    selectedMondaySlotKey,
    selectedMondaySlotObj,
    availabilitySlots,
  } = params;

  if (!selectedDate) return [];
  const warnings: string[] = [];

  const pushSlotWarnings = (slot: TimeSlot, label: string) => {
    if (slot.is_closed) {
      warnings.push(`${label} is marked closed (inquiry only). Manual booking will override.`);
    } else if (slot.sold_out) {
      warnings.push(`${label} already has the maximum number of classes for that session — manual booking will add another (faculty override).`);
    }
    // Real student-seat check — independent of faculty-slot availability.
    if (maxStudents != null && studentCount > maxStudents) {
      warnings.push(`${label} exceeds the per-class limit of ${maxStudents} students by ${studentCount - maxStudents}.`);
    }
  };

  if (isSelectedDateMonday) {
    // Monday: check the chosen slot
    if (selectedMondaySlotObj) {
      pushSlotWarnings(selectedMondaySlotObj, selectedMondaySlotKey);
    }
  } else {
    const targets = sessionMode === 'both' ? ['9:00 AM', '11:00 AM'] : [SESSION_SLOT_KEY[sessionMode as 'session_1' | 'session_2']];
    for (const ts of targets) {
      const slot = availabilitySlots.find((s) => s.date === selectedDate && s.time_slot === ts);
      if (!slot) continue;
      const label = sessionMode === 'both' ? `${ts}` : 'this date';
      pushSlotWarnings(slot, label);
    }
  }
  return warnings;
}

type PricingTierKey = 'standard' | 'tier_1' | 'tier_2' | 'dual_option' | 'hosted';

interface ClassItem {
  id: string;
  tour_stop_id: string;
  class_option_id: string;
  class_option_name: string;
  scheduled_date: string;
  /** Optional — absent for Monday bookings where exact time is used instead */
  session_identifier?: 'session_1' | 'session_2';
  /** 12h Monday slot key (e.g. "6:00 PM") — present for Monday classes */
  time_slot?: string;
  /** Exact 24h start time from availability (HH:MM:SS) — present for Monday classes */
  start_time?: string;
  /** End time from availability (HH:MM:SS) — present for Monday classes, display-only */
  end_time?: string;
  student_count: number;
  /** Required on every class (SDA-274) — stored as classes.class_level. */
  student_level: SelectableClassLevel;
  special_requests: string;
  price_cents: number;
  travel_surcharge_cents: number;
  pricing_tier: PricingTierKey | null;
}

const PRICING_TIER_LABELS: Record<PricingTierKey, string> = {
  standard: 'Standard',
  tier_1: 'Tier 1',
  tier_2: 'Tier 2',
  dual_option: 'Dual Option',
  hosted: 'Hosted — Inquiry Required',
};

/**
 * Three-button class-level picker for the Add Class modal. Mirrors the studio
 * -facing picker in components/events/TimeSlotSelector so admins and studios
 * see the same levels and age ranges. No "All Levels" option and no default —
 * the admin has to choose.
 */
function ClassLevelPicker({
  value,
  onChange,
}: {
  value: '' | SelectableClassLevel;
  onChange: (level: SelectableClassLevel) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {CLASS_LEVEL_PICKER_OPTIONS.map((opt) => {
        const isLevelSelected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`
              flex-1 py-1.5 px-1 rounded-md text-center transition-all text-xs font-medium
              ${isLevelSelected
                ? 'bg-primary-500 text-white shadow-sm'
                : 'bg-warm-100 text-warm-600 hover:bg-warm-200 hover:text-warm-800'
              }
            `}
          >
            <div>{opt.label}</div>
            <div className={`text-[10px] font-normal mt-0.5 ${isLevelSelected ? 'text-primary-100' : 'text-warm-400'}`}>
              {opt.ages}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function BookingFormPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Confirm-before-create modal
  const [showConfirmCreate, setShowConfirmCreate] = useState(false);

  // Today in YYYY-MM-DD (local), used for Class Date min= floor.
  const todayIso = new Date().toLocaleDateString('en-CA'); // en-CA formats as YYYY-MM-DD

  // Reference data
  const [tourStops, setTourStops] = useState<TourStop[]>([]);

  // Studio search (server-side — admin /studios list caps limit at 100, and the
  // list endpoint doesn't return contact data, so client-side filtering both
  // truncates results AND misses email/contact-name matches).
  const [studioId, setStudioId] = useState('');
  const [selectedStudio, setSelectedStudio] = useState<Studio | null>(null);
  const [studioSearch, setStudioSearch] = useState('');
  const [searchedStudios, setSearchedStudios] = useState<Studio[]>([]);
  const [isSearchingStudios, setIsSearchingStudios] = useState(false);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [includeMarketing, setIncludeMarketing] = useState(false);
  const [includeTickets, setIncludeTickets] = useState(false);
  const [notes, setNotes] = useState('');
  const [paymentMode, setPaymentMode] = useState<'confirmed' | 'deposit_request'>('confirmed');

  // Add class modal
  const [showAddClass, setShowAddClass] = useState(false);
  const [tourStopSearch, setTourStopSearch] = useState('');
  const [isTourStopDropdownOpen, setIsTourStopDropdownOpen] = useState(false);
  const [selectedTourStopId, setSelectedTourStopId] = useState('');
  const [selectedClassOptionId, setSelectedClassOptionId] = useState('');
  const [showClassOptions, setShowClassOptions] = useState<ClassOption[]>([]);
  const [isLoadingClassOptions, setIsLoadingClassOptions] = useState(false);
  const [currentTravelSurchargeCents, setCurrentTravelSurchargeCents] = useState(0);
  const [currentDistanceMiles, setCurrentDistanceMiles] = useState<number | null>(null);
  // Pricing-tier label from the public pricing endpoint. Surfaces "Hosted —
  // Inquiry Required" so the admin doesn't accidentally book a tour stop that
  // is too far for direct booking under the standard tier model.
  const [currentPricingTier, setCurrentPricingTier] = useState<{
    tier: 'standard' | 'tier_1' | 'tier_2' | 'dual_option' | 'hosted';
    description: string;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [sessionMode, setSessionMode] = useState<SessionMode>('session_1');
  const [manualSimultaneous, setManualSimultaneous] = useState(false);
  const [studentCount, setStudentCount] = useState('15');
  // Class level (SDA-274). levelSession1 covers single-session and Monday
  // classes as well as session 1 of "both"; levelSession2 is only used when
  // sessionMode === 'both' on a Sat/Sun date.
  const [levelSession1, setLevelSession1] = useState<'' | SelectableClassLevel>('');
  const [levelSession2, setLevelSession2] = useState<'' | SelectableClassLevel>('');
  const [specialRequests, setSpecialRequests] = useState('');

  // System settings + simultaneous tracking — applies to the whole booking.
  const [systemSettings, setSystemSettings] = useState<PublicSystemSettings | null>(null);
  const [bookingSimultaneous, setBookingSimultaneous] = useState(false);
  const [bookingSimultaneousTrigger, setBookingSimultaneousTrigger] = useState<SimultaneousTrigger>(null);

  // Capacity awareness — non-blocking, informational only.
  const [availabilitySlots, setAvailabilitySlots] = useState<TimeSlot[]>([]);

  // Monday slot selection in Add Class modal
  const [selectedMondaySlotKey, setSelectedMondaySlotKey] = useState<string>('');

  // Studio origin coords resolved from the studio's zip via the public pricing
  // endpoint (studios table has no lat/lng columns). Used to rank tour stops
  // by distance in the Add Class typeahead.
  const [studioCoords, setStudioCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    loadReferenceData();
  }, []);

  // Fetch travel surcharge for the selected studio + tour stop combination so
  // it can be displayed in the Add Class modal and stored on the ClassItem.
  // Reuses the public /events/:id/pricing endpoint with the studio's zip_code.
  useEffect(() => {
    if (!selectedTourStopId || !selectedStudio?.zip_code) {
      setCurrentTravelSurchargeCents(0);
      setCurrentDistanceMiles(null);
      setCurrentPricingTier(null);
      return;
    }
    let cancelled = false;
    getEventPricing(selectedTourStopId, { zip: selectedStudio.zip_code })
      .then((pricing) => {
        if (cancelled) return;
        setCurrentTravelSurchargeCents(pricing.pricing_tier.surcharge_cents);
        setCurrentDistanceMiles(pricing.distance_miles);
        setCurrentPricingTier({
          tier: pricing.pricing_tier.tier,
          description: pricing.pricing_tier.description,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCurrentTravelSurchargeCents(0);
        setCurrentDistanceMiles(null);
        setCurrentPricingTier(null);
      });
    return () => { cancelled = true; };
  }, [selectedStudio, selectedTourStopId]);

  // Resolve the studio's origin coords (lat/lng from its zip) by piggybacking
  // on the public pricing endpoint, which already geocodes the zip and returns
  // origin_coords. One call powers the closest-first distance ranking of the
  // tour-stop typeahead. studios table itself has no lat/lng columns.
  useEffect(() => {
    const anchorTourStopId = tourStops[0]?.id;
    if (!selectedStudio?.zip_code || !anchorTourStopId) {
      setStudioCoords(null);
      return;
    }
    let cancelled = false;
    getEventPricing(anchorTourStopId, { zip: selectedStudio.zip_code })
      .then((pricing) => {
        if (cancelled) return;
        setStudioCoords(pricing.origin_coords ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setStudioCoords(null);
      });
    return () => { cancelled = true; };
  }, [selectedStudio, tourStops]);

  // Load class options for the selected tour stop's show. Auto-selects when
  // the show has exactly one option (common case).
  useEffect(() => {
    if (!selectedTourStopId) {
      setShowClassOptions([]);
      setSelectedClassOptionId('');
      return;
    }
    const tourStop = tourStops.find((ts) => ts.id === selectedTourStopId);
    if (!tourStop?.show_id) {
      setShowClassOptions([]);
      setSelectedClassOptionId('');
      return;
    }
    let cancelled = false;
    setIsLoadingClassOptions(true);
    getShow(tourStop.show_id)
      .then((show) => {
        if (cancelled) return;
        const options = (show.class_options || []).filter((o) => o.is_active);
        setShowClassOptions(options);
        setSelectedClassOptionId(options.length === 1 ? options[0]!.id : '');
      })
      .catch(() => {
        if (cancelled) return;
        setShowClassOptions([]);
        setSelectedClassOptionId('');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClassOptions(false);
      });
    return () => { cancelled = true; };
  }, [selectedTourStopId, tourStops]);

  const loadReferenceData = async () => {
    try {
      setIsLoading(true);
      const [tourStopsData, settings] = await Promise.all([
        getAllTourStops({ is_active: true, time_filter: 'upcoming' }),
        getPublicSystemSettings().catch(() => null),
      ]);
      setTourStops(tourStopsData.tour_stops);
      setSystemSettings(settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch availability slots for the selected tour stop. Used by the capacity
  // warning — non-blocking; missing data just silences the warning.
  useEffect(() => {
    if (!selectedTourStopId) {
      setAvailabilitySlots([]);
      return;
    }
    let cancelled = false;
    getEventAvailability(selectedTourStopId)
      .then((res) => { if (!cancelled) setAvailabilitySlots(res.slots); })
      .catch(() => { if (!cancelled) setAvailabilitySlots([]); });
    return () => { cancelled = true; };
  }, [selectedTourStopId]);

  // Auto-detect simultaneous-sessions trigger. Distance ≥ 40mi (when "Both
  // sessions" is selected) and matinee threshold override the manual toggle.
  // Trigger precedence: distance_threshold > matinee_threshold > manual_checkbox.
  // Reset Monday slot key when the date changes
  useEffect(() => {
    setSelectedMondaySlotKey('');
  }, [selectedDate]);

  useEffect(() => {
    if (sessionMode !== 'both') {
      setBookingSimultaneous(false);
      setBookingSimultaneousTrigger(null);
      return;
    }
    if (currentDistanceMiles !== null && currentDistanceMiles >= 40) {
      setBookingSimultaneous(true);
      setBookingSimultaneousTrigger('distance_threshold');
      return;
    }
    const tourStop = tourStops.find((ts) => ts.id === selectedTourStopId);
    if (tourStop && systemSettings && isMatineeThresholdTriggered(
      tourStop.matinee_time,
      systemSettings.matinee_threshold_time,
      systemSettings.default_matinee_time,
    )) {
      setBookingSimultaneous(true);
      setBookingSimultaneousTrigger('matinee_threshold');
      return;
    }
    if (manualSimultaneous) {
      setBookingSimultaneous(true);
      setBookingSimultaneousTrigger('manual_checkbox');
      return;
    }
    setBookingSimultaneous(false);
    setBookingSimultaneousTrigger(null);
  }, [sessionMode, currentDistanceMiles, selectedTourStopId, tourStops, systemSettings, manualSimultaneous]);

  // Debounced server-side studio search. Hits /api/admin/studios?search=<q>,
  // which matches studio name/city/zip AND joins through contacts for
  // first_name/last_name/email — handles the case where the admin types a
  // contact name or email instead of the studio name.
  useEffect(() => {
    const q = studioSearch.trim();
    if (!q) {
      setSearchedStudios([]);
      setIsSearchingStudios(false);
      return;
    }
    let cancelled = false;
    setIsSearchingStudios(true);
    const handle = setTimeout(() => {
      getStudios({ search: q, limit: 20 })
        .then((res) => {
          if (cancelled) return;
          setSearchedStudios(res.studios);
        })
        .catch(() => {
          if (cancelled) return;
          setSearchedStudios([]);
        })
        .finally(() => {
          if (!cancelled) setIsSearchingStudios(false);
        });
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [studioSearch]);

  // Pre-compute a haversine distance per tour stop when we know the studio's
  // geocoded origin coords (resolved from its zip via the pricing endpoint —
  // studios table has no lat/lng columns of its own).
  const tourStopsWithDistance: Array<TourStop & { distanceMiles: number | null }> = (() => {
    return tourStops.map((ts) => {
      const lat = ts.theater?.latitude;
      const lng = ts.theater?.longitude;
      const distanceMiles = studioCoords && lat != null && lng != null
        ? haversineMiles(studioCoords, { lat, lng })
        : null;
      return Object.assign({}, ts, { distanceMiles });
    });
  })();

  const filteredTourStops = (() => {
    const q = tourStopSearch.trim().toLowerCase();
    const matched = q
      ? tourStopsWithDistance.filter((ts) => {
          const show = ts.show?.name?.toLowerCase() || '';
          const city = ts.city?.name?.toLowerCase() || '';
          const state = ts.city?.state?.toLowerCase() || '';
          const theater = ts.theater?.name?.toLowerCase() || '';
          return (
            show.includes(q) ||
            city.includes(q) ||
            state.includes(q) ||
            theater.includes(q)
          );
        })
      : tourStopsWithDistance;
    // Sort by distance ascending when a studio is picked (closest first).
    // Fall back to start_date asc when no studio is selected.
    const hasDistance = matched.some((ts) => ts.distanceMiles !== null);
    if (hasDistance) {
      return [...matched].sort((a, b) => {
        if (a.distanceMiles === null) return 1;
        if (b.distanceMiles === null) return -1;
        return a.distanceMiles - b.distanceMiles;
      });
    }
    return [...matched].sort((a, b) => a.start_date.localeCompare(b.start_date));
  })();

  const selectedTourStop = tourStops.find((ts) => ts.id === selectedTourStopId);

  // Detect if the selected date is a Monday (drives slot picker vs. session radio)
  const isSelectedDateMonday = selectedDate
    ? new Date(selectedDate + 'T00:00:00').getDay() === 1
    : false;

  // Build the session-time config used by the shared engine for radio labels.
  // Mirrors the config shape in TimeSlotSelector.tsx. Null when systemSettings
  // is not yet loaded — callers fall back to static strings while null.
  const sessionTimeConfig = useMemo(() => {
    if (!systemSettings) return null;
    return {
      defaultSessionStartTime: systemSettings.default_session_start_time,
      matineeTime: selectedTourStop?.matinee_time ?? null,
      sessionDurationMinutes: systemSettings.session_duration_minutes,
      matineeThresholdTime: systemSettings.matinee_threshold_time,
      defaultMatineeTime: systemSettings.default_matinee_time,
    };
  }, [
    systemSettings?.default_session_start_time,
    systemSettings?.session_duration_minutes,
    systemSettings?.matinee_threshold_time,
    systemSettings?.default_matinee_time,
    selectedTourStop?.matinee_time,
  ]);

  // Compute human-readable radio labels from the shared engine (preference 'default').
  // Falls back to static strings while sessionTimeConfig is null (settings loading).
  const radioLabels = useMemo((): {
    single: string;
    second_slot: string;
    both: string;
  } => {
    if (!sessionTimeConfig) {
      return {
        single: '9:00 AM only',
        second_slot: '11:00 AM only',
        both: 'Both sessions (9 AM + 11 AM) — creates two classes',
      };
    }
    const s1 = resolveSessionTimes(sessionTimeConfig, 'default', 'single');
    const s2 = resolveSessionTimes(sessionTimeConfig, 'default', 'second_slot');
    const bothMode = bookingSimultaneous ? 'simultaneous' : 'back_to_back';
    const both: ResolvedSessionTimes = resolveSessionTimes(sessionTimeConfig, 'default', bothMode);
    const fmt = (t: { start: string; end: string }) =>
      `${formatTimeDisplay(t.start)} – ${formatTimeDisplay(t.end)}`;
    const bothLabel = both.isSimultaneous
      ? `Both sessions (simultaneous) — ${fmt(both.session1)} (creates two classes)`
      : `Both sessions — ${fmt(both.session1)} + ${fmt(both.session2)} (creates two classes)`;
    return {
      single: `Session 1 — ${fmt(s1.session1)}`,
      second_slot: `Session 2 — ${fmt(s2.session2)}`,
      both: bothLabel,
    };
  }, [sessionTimeConfig, bookingSimultaneous]);

  // Monday slots for the selected date from availability
  const mondaySlotsForDate = isSelectedDateMonday
    ? availabilitySlots.filter((s) => s.date === selectedDate && s.day_type === 'monday')
    : [];

  // The selected Monday slot object (for capacity checks + handleAddClass)
  const selectedMondaySlotObj = selectedMondaySlotKey
    ? mondaySlotsForDate.find((s) => s.time_slot === selectedMondaySlotKey)
    : undefined;

  // Capacity awareness — derive informational warnings for the picked date.
  // Non-blocking by design: admin manual bookings bypass capacity. Surface
  // is_closed (booking window), sold_out (faculty/session-slot exhaustion), or
  // per-class student-seat overflow so the admin can confirm the override.
  // SDA-227: the student check compares against the class option's real
  // max_students (max_participants), NOT the availability API's `remaining`
  // (which counts faculty class-slots, not student seats).
  const capacityWarnings: string[] = computeCapacityWarnings({
    selectedDate,
    isSelectedDateMonday,
    sessionMode,
    studentCount: parseInt(studentCount) || 0,
    maxStudents: showClassOptions.find((o) => o.id === selectedClassOptionId)?.max_participants,
    selectedMondaySlotKey,
    selectedMondaySlotObj,
    availabilitySlots,
  });

  const handleAddClass = () => {
    if (!selectedTourStopId || !selectedClassOptionId || !selectedDate || !studentCount) return;
    // Level is required for every class. Guarding before the items are built
    // keeps student_level narrowed to a real level rather than '' .
    if (!levelSession1) return;

    const tourStop = tourStops.find((ts) => ts.id === selectedTourStopId);
    if (!tourStop) return;
    const classOption = showClassOptions.find((o) => o.id === selectedClassOptionId);
    if (!classOption) return;

    let newItems: ClassItem[];

    if (isSelectedDateMonday) {
      // Monday: one ClassItem with exact slot time, no session_identifier
      if (!selectedMondaySlotKey) return; // slot must be selected
      newItems = [{
        id: crypto.randomUUID(),
        tour_stop_id: selectedTourStopId,
        class_option_id: selectedClassOptionId,
        class_option_name: classOption.name,
        scheduled_date: selectedDate,
        session_identifier: undefined,
        time_slot: selectedMondaySlotKey,
        start_time: selectedMondaySlotObj?.start_time,
        end_time: selectedMondaySlotObj?.end_time,
        student_count: parseInt(studentCount),
        student_level: levelSession1,
        special_requests: specialRequests,
        price_cents: classOption.price_cents,
        travel_surcharge_cents: currentTravelSurchargeCents,
        pricing_tier: currentPricingTier?.tier ?? null,
      }];
    } else {
      const identifiers: ('session_1' | 'session_2')[] =
        sessionMode === 'both' ? ['session_1', 'session_2'] : [sessionMode as 'session_1' | 'session_2'];

      // "Both" books two independent classes, so each session carries its own
      // level. Single-session modes use levelSession1 whichever slot they hit.
      let session2Level: SelectableClassLevel = levelSession1;
      if (sessionMode === 'both') {
        if (!levelSession2) return;
        session2Level = levelSession2;
      }

      newItems = identifiers.map((ident) => ({
        id: crypto.randomUUID(),
        tour_stop_id: selectedTourStopId,
        class_option_id: selectedClassOptionId,
        class_option_name: classOption.name,
        scheduled_date: selectedDate,
        session_identifier: ident,
        student_count: parseInt(studentCount),
        student_level: ident === 'session_2' ? session2Level : levelSession1,
        special_requests: specialRequests,
        price_cents: classOption.price_cents,
        travel_surcharge_cents: currentTravelSurchargeCents,
        pricing_tier: currentPricingTier?.tier ?? null,
      }));
    }

    setClasses([...classes, ...newItems]);
    resetAddClassForm();
    setShowAddClass(false);
  };

  const resetAddClassForm = () => {
    setTourStopSearch('');
    setIsTourStopDropdownOpen(false);
    setSelectedTourStopId('');
    setSelectedClassOptionId('');
    setShowClassOptions([]);
    setCurrentTravelSurchargeCents(0);
    setCurrentDistanceMiles(null);
    setCurrentPricingTier(null);
    setSelectedDate('');
    setSessionMode('session_1');
    setManualSimultaneous(false);
    setStudentCount('15');
    setLevelSession1('');
    setLevelSession2('');
    setSpecialRequests('');
    setSelectedMondaySlotKey('');
  };

  const handleRemoveClass = (classId: string) => {
    setClasses(classes.filter((c) => c.id !== classId));
  };

  const travelSurchargeTotal = classes.reduce((sum, c) => sum + c.travel_surcharge_cents, 0);

  const calculateTotal = () => {
    const classesTotal = classes.reduce((sum, c) => sum + c.price_cents, 0);
    const marketingFee = includeMarketing ? 5000 : 0;
    return classesTotal + travelSurchargeTotal + marketingFee;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!studioId) {
      setError('Please select a studio');
      return;
    }
    if (classes.length === 0) {
      setError('Please add at least one class');
      return;
    }

    // Show the confirmation modal; the actual API call runs in handleConfirmCreate.
    setShowConfirmCreate(true);
  };

  const handleConfirmCreate = async () => {
    // Guard against double-submit: if already saving, do nothing.
    if (isSaving) return;
    setShowConfirmCreate(false);

    try {
      setIsSaving(true);

      const booking = await createManualBooking({
        studio_id: studioId,
        tour_stop_id: classes[0]?.tour_stop_id || '', // All classes on a manual booking share one tour stop
        classes: classes.map((c) => ({
          class_option_id: c.class_option_id,
          scheduled_date: c.scheduled_date,
          session_identifier: c.session_identifier,
          time_slot: c.time_slot,
          start_time: c.start_time,
          student_count: c.student_count,
          student_level: c.student_level,
          special_requests: c.special_requests || undefined,
        })),
        marketing_materials: includeMarketing,
        group_ticket_inquiry: includeTickets,
        notes: notes || undefined,
        simultaneous_sessions: bookingSimultaneous,
        simultaneous_trigger: bookingSimultaneousTrigger,
        start_time_preference: 'default',
        payment_mode: paymentMode,
      });

      navigate(`/admin/bookings/${booking.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create booking');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse mb-8" />
        <div className="bg-white rounded-xl shadow-sm p-8 animate-pulse">
          <div className="space-y-6">
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/bookings"
          className="inline-flex items-center text-sm text-warm-600 hover:text-warm-900 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Bookings
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          Create Manual Booking
        </h1>
        <p className="text-warm-600 mt-1">
          Create a booking on behalf of a studio.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Studio Selection */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Studio
              </h2>

              {error && (
                <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                  {error}
                </div>
              )}

              {selectedStudio ? (
                <div className="flex items-center justify-between p-4 bg-warm-50 rounded-lg">
                  <div>
                    <p className="font-medium text-warm-900">{selectedStudio.name}</p>
                    <p className="text-sm text-warm-500">
                      {selectedStudio.city}, {selectedStudio.state} {selectedStudio.zip_code}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStudioId('');
                      setSelectedStudio(null);
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={studioSearch}
                    onChange={(e) => setStudioSearch(e.target.value)}
                    placeholder="Search by studio name, city, zip, contact name, or email..."
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {studioSearch && (isSearchingStudios || searchedStudios.length > 0) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-warm-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {isSearchingStudios && searchedStudios.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-warm-500">Searching...</div>
                      ) : (
                        searchedStudios.map((studio) => (
                          <button
                            key={studio.id}
                            type="button"
                            onClick={() => {
                              setStudioId(studio.id);
                              setSelectedStudio(studio);
                              setStudioSearch('');
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-warm-50 border-b border-warm-100 last:border-0"
                          >
                            <p className="font-medium text-warm-900">{studio.name}</p>
                            <p className="text-sm text-warm-500">
                              {studio.city}, {studio.state} {studio.zip_code}
                            </p>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                  {studioSearch && !isSearchingStudios && searchedStudios.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-warm-200 rounded-lg shadow-lg px-4 py-3">
                      <p className="text-sm font-medium text-warm-900">
                        No studios match &ldquo;{studioSearch}&rdquo;
                      </p>
                      <p className="text-xs text-warm-500 mt-1">
                        Searched studio name, city, zip code, and contact name/email. Adjust the search or contact support to add the studio.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Classes */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-warm-900">
                  Classes ({classes.length})
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    // Pre-select the locked tour stop when additional classes are added
                    if (classes.length > 0 && classes[0]) {
                      setSelectedTourStopId(classes[0].tour_stop_id);
                    }
                    setShowAddClass(true);
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add Class
                </button>
              </div>

              {classes.length === 0 ? (
                <div className="text-center py-8 text-warm-500">
                  <p>No classes added yet.</p>
                  <button
                    type="button"
                    onClick={() => setShowAddClass(true)}
                    className="mt-2 text-primary-600 hover:text-primary-700"
                  >
                    Add your first class
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {classes.map((cls) => {
                    const tourStop = tourStops.find((ts) => ts.id === cls.tour_stop_id);
                    const clsIsMonday = new Date(cls.scheduled_date + 'T00:00:00').getDay() === 1;

                    // Compute the display time for this class row.
                    // Monday items: use stored start_time/end_time directly.
                    // Sat/Sun session_identifier items: derive the real resolved time from
                    // the shared engine (same engine as backend), so the label matches
                    // what actually gets booked — not the hardcoded 9/11 AM keys.
                    let clsTimeDisplay: string;
                    if (clsIsMonday && cls.start_time) {
                      // Monday: exact stored time
                      clsTimeDisplay = `${formatTimeDisplay(cls.start_time)}${cls.end_time ? ` – ${formatTimeDisplay(cls.end_time)}` : ''}`;
                    } else if (cls.session_identifier && sessionTimeConfig) {
                      // Sat/Sun: resolve via shared engine using sibling context for this date
                      const siblingClasses = classes.filter((c) => c.scheduled_date === cls.scheduled_date);
                      const mode = deriveBookingMode(
                        siblingClasses.map((c) => ({
                          sessionIdentifier: c.session_identifier,
                          timeSlot: c.time_slot,
                          startTime: c.start_time,
                          isMultiSlotDate: false,
                        })),
                        bookingSimultaneous,
                      );
                      // resolveBookingClassTimes needs the resolved session times for this mode
                      const resolvedForRow = resolveSessionTimes(sessionTimeConfig, 'default', mode);
                      const times = resolveBookingClassTimes(
                        {
                          sessionIdentifier: cls.session_identifier,
                          timeSlot: cls.time_slot,
                          startTime: cls.start_time,
                        },
                        false,
                        resolvedForRow,
                        sessionTimeConfig.sessionDurationMinutes,
                      );
                      clsTimeDisplay = `${formatTimeDisplay(times.start)} – ${formatTimeDisplay(times.end)}`;
                    } else if (cls.start_time) {
                      // Fallback: start_time present but no session_identifier (Monday without config)
                      clsTimeDisplay = `${formatTimeDisplay(cls.start_time)}${cls.end_time ? ` – ${formatTimeDisplay(cls.end_time)}` : ''}`;
                    } else {
                      // Last fallback: no config loaded yet, show slot key
                      clsTimeDisplay = cls.time_slot ?? '—';
                    }

                    const level = formatClassLevel(cls.student_level);
                    const levelDisplay = level.ages ? `${level.label} (${level.ages})` : level.label;

                    return (
                      <div key={cls.id} className="flex items-center justify-between p-4 bg-warm-50 rounded-lg">
                        <div>
                          <p className="font-medium text-warm-900">
                            {tourStop?.show?.name}
                          </p>
                          <p className="text-sm text-warm-600">
                            {cls.class_option_name} • {tourStop?.theater?.name} • {tourStop?.city?.name}
                          </p>
                          <p className="text-sm text-warm-500">
                            {new Date(cls.scheduled_date + 'T12:00:00').toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })} at {clsTimeDisplay} • {cls.student_count} students • {levelDisplay}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-warm-900">
                            {formatCurrency(cls.price_cents)}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveClass(cls.id)}
                            className="text-danger-600 hover:text-danger-700"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Options */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Options
              </h2>

              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={includeMarketing}
                  onChange={(e) => setIncludeMarketing(e.target.checked)}
                  className="w-5 h-5 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-warm-700">Include Marketing Materials</span>
                  <span className="text-sm text-warm-500 block">+$50.00</span>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={includeTickets}
                  onChange={(e) => setIncludeTickets(e.target.checked)}
                  className="w-5 h-5 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-warm-700">Group tickets (free)</span>
                  <span className="text-sm text-warm-500 block">No additional cost</span>
                </div>
              </label>

              <div className="mb-4">
                <span className="block text-sm font-medium text-warm-700 mb-2">
                  Payment
                </span>
                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_mode"
                      value="confirmed"
                      checked={paymentMode === 'confirmed'}
                      onChange={() => setPaymentMode('confirmed')}
                      className="mt-1 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-warm-700">Confirm immediately</span>
                      <span className="text-sm text-warm-500 block">
                        Booking is created as Confirmed and the studio gets a confirmation email right away. No deposit is collected up front.
                      </span>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_mode"
                      value="deposit_request"
                      checked={paymentMode === 'deposit_request'}
                      onChange={() => setPaymentMode('deposit_request')}
                      className="mt-1 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-warm-700">Request deposit</span>
                      <span className="text-sm text-warm-500 block">
                        Booking stays Pending. The studio&apos;s primary contact gets an email with a link to pay the deposit online; the booking confirms automatically when they pay.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Internal Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Notes about this manual booking..."
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="order-first lg:order-none space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Booking Summary
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-warm-600">Classes ({classes.length})</span>
                  <span className="text-warm-900">
                    {formatCurrency(classes.reduce((sum, c) => sum + c.price_cents, 0))}
                  </span>
                </div>
                {classes[0]?.pricing_tier && (
                  <div className="flex justify-between">
                    <span className="text-warm-600">
                      Travel Surcharge
                      <span className="text-warm-500 text-xs"> ({PRICING_TIER_LABELS[classes[0].pricing_tier]})</span>
                    </span>
                    <span className="text-warm-900">{formatCurrency(travelSurchargeTotal)}</span>
                  </div>
                )}
                {includeMarketing && (
                  <div className="flex justify-between">
                    <span className="text-warm-600">Marketing Materials</span>
                    <span className="text-warm-900">{formatCurrency(5000)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-warm-200">
                  <span className="font-semibold text-warm-900">Total</span>
                  <span className="font-semibold text-warm-900">
                    {formatCurrency(calculateTotal())}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isSaving || !studioId || classes.length === 0}
                  className="w-full btn-primary disabled:opacity-50"
                >
                  {isSaving ? 'Creating...' : 'Create Booking'}
                </button>
                <Link
                  to="/admin/bookings"
                  className="block w-full text-center px-4 py-2.5 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Confirm Create Modal — shown before the API call fires */}
      {showConfirmCreate && (
        <ConfirmModal
          title="Create booking and notify studio?"
          message={
            paymentMode === 'deposit_request' ? (
              <>
                <p>
                  This booking will be created as <strong>Pending</strong>.
                </p>
                <ul className="list-disc pl-4 mt-2 space-y-1">
                  <li>A deposit payment request is emailed to the studio's primary contact immediately.</li>
                  <li>When they pay online, the booking confirms automatically and their card is saved.</li>
                  <li>The remaining balance is auto-charged 15 days before the class.</li>
                </ul>
                <p className="mt-2">
                  The studio needs a portal login — they can register with their contact email if they
                  don't have one.
                </p>
              </>
            ) : (
              'This booking will be created as Confirmed. A confirmation email is sent to the studio\'s primary contact immediately. If the studio has a saved card, the remaining balance is auto-charged 15 days before the class — record offline payments with "Mark balance paid" to prevent that.'
            )
          }
          confirmLabel="Create Booking"
          onConfirm={handleConfirmCreate}
          onCancel={() => setShowConfirmCreate(false)}
        />
      )}

      {/* Add Class Modal */}
      {showAddClass && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold text-warm-900 mb-4">Add Class</h3>

            <div className="space-y-4">
              {/* Tour Stop searchable picker */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Tour Stop *
                </label>
                {selectedTourStop ? (
                  <div>
                    <div className="flex items-center justify-between p-3 bg-warm-50 rounded-lg border border-warm-200">
                      <div className="min-w-0">
                        <p className="font-medium text-warm-900 truncate">{selectedTourStop.show?.name}</p>
                        <p className="text-sm text-warm-500 truncate">
                          {selectedTourStop.theater?.name} • {selectedTourStop.city?.name}, {selectedTourStop.city?.state}
                        </p>
                        <p className="text-xs text-warm-400">
                          {parseLocalDate(selectedTourStop.start_date).toLocaleDateString()} – {parseLocalDate(selectedTourStop.end_date).toLocaleDateString()}
                        </p>
                      </div>
                      {classes.length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTourStopId('');
                            setTourStopSearch('');
                          }}
                          className="text-sm text-primary-600 hover:text-primary-700 shrink-0 ml-3"
                        >
                          Change
                        </button>
                      )}
                    </div>
                    {classes.length > 0 && (
                      <p className="text-xs text-warm-500 mt-1">
                        All classes in a booking share one tour stop — remove existing classes to change.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      value={tourStopSearch}
                      onChange={(e) => {
                        setTourStopSearch(e.target.value);
                        setIsTourStopDropdownOpen(true);
                      }}
                      onFocus={() => setIsTourStopDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setIsTourStopDropdownOpen(false), 150)}
                      placeholder="Search by show, city, state, or theater..."
                      required
                      className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {isTourStopDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-warm-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {filteredTourStops.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-warm-500">
                            No tour stops match &ldquo;{tourStopSearch}&rdquo;.
                          </div>
                        ) : (
                          filteredTourStops.slice(0, 20).map((ts) => (
                            <button
                              key={ts.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setSelectedTourStopId(ts.id);
                                setTourStopSearch('');
                                setIsTourStopDropdownOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-warm-50 border-b border-warm-100 last:border-0"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="font-medium text-warm-900">{ts.show?.name}</p>
                                  <p className="text-sm text-warm-500">
                                    {ts.theater?.name} • {ts.city?.name}, {ts.city?.state}
                                  </p>
                                  <p className="text-xs text-warm-400">
                                    {parseLocalDate(ts.start_date).toLocaleDateString()} – {parseLocalDate(ts.end_date).toLocaleDateString()}
                                  </p>
                                </div>
                                {ts.distanceMiles !== null && (
                                  <span className="shrink-0 px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                                    {ts.distanceMiles.toFixed(0)} mi
                                  </span>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Class Type */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Class Type *
                </label>
                <select
                  value={selectedClassOptionId}
                  onChange={(e) => setSelectedClassOptionId(e.target.value)}
                  required
                  disabled={!selectedTourStopId || isLoadingClassOptions}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-warm-50 disabled:text-warm-400"
                >
                  <option value="">
                    {!selectedTourStopId
                      ? 'Select a tour stop first'
                      : isLoadingClassOptions
                        ? 'Loading class types...'
                        : showClassOptions.length === 0
                          ? 'No class types available for this show'
                          : 'Select a class type'}
                  </option>
                  {showClassOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name} ({formatCurrency(opt.price_cents)})
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Class Date *
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                  min={maxIsoDate(selectedTourStop?.start_date, todayIso)}
                  max={selectedTourStop?.end_date}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {selectedTourStop && (
                  <p className="text-sm text-warm-500 mt-1">
                    Available: {parseLocalDate(selectedTourStop.start_date).toLocaleDateString()} - {parseLocalDate(selectedTourStop.end_date).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Session selection — Monday slot picker or standard Sat/Sun radio */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  {isSelectedDateMonday ? 'Monday Time Slot *' : 'Session(s) *'}
                </label>
                {isSelectedDateMonday ? (
                  // Monday: show a slot picker built from availability
                  <select
                    value={selectedMondaySlotKey}
                    onChange={(e) => setSelectedMondaySlotKey(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">
                      {mondaySlotsForDate.length === 0
                        ? 'No Monday slots available for this date'
                        : 'Select a time slot'}
                    </option>
                    {mondaySlotsForDate.map((slot) => (
                      <option key={slot.time_slot} value={slot.time_slot}>
                        {slot.start_time && slot.end_time
                          ? `${formatTimeDisplay(slot.start_time)} – ${formatTimeDisplay(slot.end_time)}`
                          : slot.time_slot}{' '}
                        ({slot.sold_out ? 'Sold out' : `${slot.remaining} remaining`})
                      </option>
                    ))}
                  </select>
                ) : (
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="session_1"
                      checked={sessionMode === 'session_1'}
                      onChange={() => setSessionMode('session_1')}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span>{radioLabels.single}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="session_2"
                      checked={sessionMode === 'session_2'}
                      onChange={() => setSessionMode('session_2')}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span>{radioLabels.second_slot}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="both"
                      checked={sessionMode === 'both'}
                      onChange={() => setSessionMode('both')}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span>{radioLabels.both}</span>
                  </label>
                </div>
                )}

                {/* Simultaneous toggle + banners — only when "Both" is picked for Sat/Sun. */}
                {!isSelectedDateMonday && sessionMode === 'both' && (
                  <div className="mt-3 space-y-2">
                    {bookingSimultaneousTrigger === 'distance_threshold' && currentDistanceMiles !== null && (
                      <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800">
                        <strong>Simultaneous required:</strong> studio is {currentDistanceMiles.toFixed(1)} mi from venue (≥ 40 mi threshold). Both classes will run at the same time, requiring two faculty.
                      </div>
                    )}
                    {bookingSimultaneousTrigger === 'matinee_threshold' && (() => {
                      const ts = tourStops.find((t) => t.id === selectedTourStopId);
                      const matinee = ts?.matinee_time || systemSettings?.default_matinee_time;
                      return (
                        <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800">
                          <strong>Simultaneous required:</strong> matinee at {matinee?.slice(0, 5) || 'early'} crosses the threshold. Both classes will run at the same time, requiring two faculty.
                        </div>
                      );
                    })()}
                    <label className={`flex items-center gap-2 ${bookingSimultaneousTrigger === 'distance_threshold' || bookingSimultaneousTrigger === 'matinee_threshold' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={bookingSimultaneous}
                        onChange={(e) => setManualSimultaneous(e.target.checked)}
                        disabled={bookingSimultaneousTrigger === 'distance_threshold' || bookingSimultaneousTrigger === 'matinee_threshold'}
                        className="w-4 h-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-warm-700">
                        Simultaneous sessions (2 faculty, same time)
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Class Level — required. "Both" books two classes, so it gets
                  one picker per session. */}
              <div>
                {!isSelectedDateMonday && sessionMode === 'both' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-warm-700 mb-1">
                        Session 1 Level *
                      </label>
                      <ClassLevelPicker value={levelSession1} onChange={setLevelSession1} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-warm-700 mb-1">
                        Session 2 Level *
                      </label>
                      <ClassLevelPicker value={levelSession2} onChange={setLevelSession2} />
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-warm-700 mb-1">
                      Class Level *
                    </label>
                    <ClassLevelPicker value={levelSession1} onChange={setLevelSession1} />
                  </>
                )}
              </div>

              {/* Student Count */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Number of Students *
                </label>
                <input
                  type="number"
                  value={studentCount}
                  onChange={(e) => setStudentCount(e.target.value)}
                  required
                  min="1"
                  max="50"
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Special Requests
                </label>
                <textarea
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Any special requests or notes..."
                />
              </div>

              {/* Price Preview */}
              {selectedClassOptionId && (() => {
                const opt = showClassOptions.find((o) => o.id === selectedClassOptionId);
                if (!opt) return null;
                return (
                  <div className="p-3 bg-warm-50 rounded-lg space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-warm-600">Class Price</span>
                      <span className="font-medium text-warm-900">
                        {formatCurrency(opt.price_cents)}
                      </span>
                    </div>
                    {currentPricingTier && (
                      <div className="flex justify-between text-sm">
                        <span className="text-warm-600">
                          Travel Surcharge
                          {currentDistanceMiles != null && (
                            <span className="text-warm-500"> ({currentDistanceMiles.toFixed(1)} mi · {PRICING_TIER_LABELS[currentPricingTier.tier]})</span>
                          )}
                        </span>
                        <span className="font-medium text-warm-900">
                          {formatCurrency(currentTravelSurchargeCents)}
                        </span>
                      </div>
                    )}
                    {currentPricingTier?.tier === 'hosted' && (
                      <div className="mt-2 p-2 bg-warning-50 border border-warning-200 rounded text-xs text-warning-800">
                        <p className="font-medium">⚠ Hosted distance — inquiry required</p>
                        <p className="mt-0.5">
                          This venue is {currentDistanceMiles?.toFixed(0)} mi from the studio. Direct booking isn&apos;t normally offered at this range. Confirm with the studio before saving.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {capacityWarnings.length > 0 && (
              <div className="mt-4 p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800">
                <p className="font-medium mb-1">⚠ Capacity notice</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {capacityWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
                <p className="text-xs text-warning-700 mt-2">
                  Manual booking will proceed regardless — confirm the override with the studio.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  resetAddClassForm();
                  setShowAddClass(false);
                }}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddClass}
                disabled={!selectedTourStopId || !selectedClassOptionId || !selectedDate || !studentCount || (isSelectedDateMonday && !selectedMondaySlotKey) || !levelSession1 || (!isSelectedDateMonday && sessionMode === 'both' && !levelSession2)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                Add Class
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
